import type { MonthKey } from '@/lib/planning-types'
import { monthRange } from './month'

/**
 * Wat een maand op de as vasthoudt. `window` zijn de maanden van het venster van
 * de kaart erboven: die staan er omdat je ernaar kijkt, niet omdat deze kans ze
 * heeft. `filled` is werk: uren of een bedrag.
 */
export type MonthHold = 'window' | 'filled'

/** Waarom de buitenste maand blijft staan, op de plek van de twijfel. */
export const MONTH_HOLD_REASON: Record<MonthHold, string> = {
  window: 'Deze maand komt uit het venster hierboven; verzet het venster om anders te kijken',
  filled: 'Hier staan uren of een bedrag; inkorten is kijken en wist niets',
}

export interface MonthEdge {
  month: MonthKey
  /** Null als deze maand van de as af mag. */
  held: MonthHold | null
}

export interface MonthAxis {
  months: MonthKey[]
  /** De buitenste maanden; alleen daar valt de as in te korten. */
  start: MonthEdge | null
  end: MonthEdge | null
}

export interface MonthAxisSources {
  /** Het venster van de kaart erboven. */
  windowMonths: MonthKey[]
  /** Zelf aangezet of aangeraakt: het enige dat weer los te laten is. */
  extra: MonthKey[]
  /** Maanden met een urenregel of een bedrag. */
  filled: MonthKey[]
}

/**
 * De maandas van een enkele kans: aaneengesloten van de eerste tot en met de
 * laatste maand die ergens uit voortkomt.
 *
 * Aaneengesloten is de hele regel. Zou de as alleen de maanden tonen die een bron
 * noemt, dan staat een bedrag in februari volgend jaar meteen naast oktober en
 * leest dat als de maand erna: vier maanden verdwijnen zonder dat de as laat zien
 * dat ze er zijn. Een tijdlijn met gaten liegt, en de tafel schuift toch al
 * horizontaal, dus de lege maanden ertussen staan er gewoon.
 *
 * De regel eronder: een maand mag alleen van de as af als je hem er zelf bij hebt
 * gezet en er niets in staat. Inkorten is een kijkhandeling en hoort nooit uren of
 * bedragen te wissen, dus alles wat uit het venster of uit werk voortkomt houdt
 * zijn kolom.
 */
export function buildMonthAxis({ windowMonths, extra, filled }: MonthAxisSources): MonthAxis {
  const known = [...new Set([...windowMonths, ...extra, ...filled])].sort()
  if (known.length === 0) return { months: [], start: null, end: null }

  const months = monthRange(known[0], known[known.length - 1])

  const edge = (month: MonthKey): MonthEdge => {
    if (windowMonths.includes(month)) return { month, held: 'window' }
    if (filled.includes(month)) return { month, held: 'filled' }

    return { month, held: null }
  }

  return { months, start: edge(months[0]), end: edge(months[months.length - 1]) }
}
